import { useState } from "react";

// SVG Icons matching the reference designs
const MailIcon = () => (
  <svg className="w-4 h-4 text-[#8a9fb4]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const LockIcon = () => (
  <svg className="w-4 h-4 text-[#8a9fb4]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

// Lock icon with check/shield for confirmation
const ConfirmLockIcon = () => (
  <svg className="w-4 h-4 text-[#8a9fb4]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const EyeIcon = () => (
  <svg className="w-4 h-4 text-[#8a9fb4] hover:text-[#313C4C]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EyeOffIcon = () => (
  <svg className="w-4 h-4 text-[#8a9fb4] hover:text-[#313C4C]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a10.025 10.025 0 013.3-5.46M21 21l-18-18m18 18L3 3m18 18l-4-4m-8-2.25A3 3 0 0010 13.5m.632-6A10.011 10.011 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-2.232 4.366" />
  </svg>
);

export default function AuthInput({
  label,
  type = "text",
  name,
  value,
  placeholder,
  onChange,
  onKeyPress,
  error,
  errorMsg,
  rightLabelElement,
  ...others
}) {
  const [showPassword, setShowPassword] = useState(false);

  // Determine icon based on name or type
  let LeftIcon = null;
  if (type === "password") {
    if (name === "confirmPassword") {
      LeftIcon = <ConfirmLockIcon />;
    } else {
      LeftIcon = <LockIcon />;
    }
  } else if (name === "email" || name === "workEmail") {
    LeftIcon = <MailIcon />;
  }

  const isPassword = type === "password";
  const actualType = isPassword && showPassword ? "text" : type;

  return (
    <div className="flex flex-col w-full gap-1">
      <style>{`
        input:-webkit-autofill,
        input:-webkit-autofill:hover, 
        input:-webkit-autofill:focus, 
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px #f0f4fa inset !important;
          -webkit-text-fill-color: #1e293b !important;
        }
      `}</style>
      {label && (
        <div className="flex justify-between items-center">
          <label className="text-[12.5px] font-semibold text-slate-600">{label}</label>
          {rightLabelElement}
        </div>
      )}
      <div
        className={`relative flex items-center w-full bg-[#f0f4fa] rounded-2xl border transition-all duration-200 h-[46px] overflow-hidden ${error
            ? "border-red-400 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-100"
            : "border-[#e1e9f4] focus-within:border-[#313C4C] focus-within:ring-2 focus-within:ring-[#313C4C]/10"
          }`}
      >
        {LeftIcon && (
          <div className="pl-4 pr-1 flex items-center justify-center">
            {LeftIcon}
          </div>
        )}
        <input
          type={actualType}
          name={name}
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          onKeyPress={onKeyPress}
          className={`w-full bg-transparent border-none outline-none text-slate-800 placeholder-slate-400/80 text-[13.5px] h-full ${LeftIcon ? "pl-2" : "pl-4"
            } pr-4`}
          {...others}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="pr-4 pl-2 text-slate-400 hover:text-slate-600 focus:outline-none flex items-center justify-center h-full"
          >
            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        )}
      </div>
      {error && <span className="text-xs text-red-500 mt-0.5 ml-1 leading-none">{errorMsg}</span>}
    </div>
  );
}
